import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { PROJECT_TAB_IDS } from "@constants/global.constants";

import { WPBUTTON_VARIANTS } from "@components/WPButton";
import WPHeading from "@components/WPHeading";

import { setMldCalcReportApiResp } from "@common/MldReportSlice";
import { updateProjectActiveTab } from "@common/ProjectInfoSlice";
import CircularLoader from "@common/styles/components/circularLoader";

import { MldCancelReport } from "@features/report-new/common/components/MldCancelReport";
import { usePendingMldJob } from "@features/report-new/common/hooks/usePendingMldJob";

import { useFullScreenLoader } from "./useFullScreenLoader";

const HEADER_HEIGHT = "60px";

const Overlay = styled.div`
  position: fixed;
  top: ${HEADER_HEIGHT};
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 101;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
`;

const LoaderCard = styled.div`
  background: rgba(255, 255, 255, 0.5);
  border-radius: 2px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-width: 412px;

  .cancel-report-button {
    border: 1px solid var(--du-color-grey);
    border-radius: 24px;
  }
`;

/**
 * Rendered as a child of WPFullScreenLoader so its hooks live in a separate
 * fiber — WPFullScreenLoader itself keeps its original hook structure intact.
 * Returns null when there is no in-flight job for the current project.
 */
const MldCancelButton = ({ onHide }) => {
  const dispatch = useDispatch();
  const { currentProjectId, currentProjectJobId } = usePendingMldJob();
  const projectName = useSelector(state => state.projectInfo.data?.projectName ?? "");

  if (!currentProjectJobId) return null;

  const handleSuccess = () => {
    dispatch(setMldCalcReportApiResp({ isLoading: false, data: null, error: null }));
    dispatch(updateProjectActiveTab(PROJECT_TAB_IDS.SYSTEM_DESIGN));
    onHide();
  };

  return (
    <MldCancelReport
      jobId={currentProjectJobId}
      projectId={currentProjectId}
      projectName={projectName}
      onSuccess={handleSuccess}
      variant={WPBUTTON_VARIANTS.text}
      buttonClass='cancel-report-button'
    />
  );
};

export const WPFullScreenLoader = () => {
  const { isFullScreenLoaderVisible, fullScreenLoaderMessage, hideFullScreenLoader } = useFullScreenLoader();
  const rateLimitExpiry = useSelector(state => state.apiData.rateLimitExpiry);
  const isRateLimited = rateLimitExpiry > Date.now();

  if (!isFullScreenLoaderVisible || isRateLimited) {
    return null;
  }

  return (
    <Overlay data-test-id='wp-loader'>
      <LoaderCard>
        <CircularLoader />
        {fullScreenLoaderMessage && <WPHeading align='center' text={fullScreenLoaderMessage} fontNoto />}
        <MldCancelButton onHide={hideFullScreenLoader} />
      </LoaderCard>
    </Overlay>
  );
};
